<script>
    import RadioButton from 'views/components/radioButton/RadioButton.svelte'
    import Textarea from 'views/components/textarea/Textarea.svelte'
    import StatusCard from 'views/components/statusCard/StatusCard.svelte'

    export let activity;
    export let rubric
    export let readOnly = true
    export let evaluation

    // Optional: Only needed for teacher for evaluating a student
    export let studentId = null;

    let xhr;
    let rubricEvaluation = evaluation?.evaluation_data || [];
    let isSyncing = false;

    const rubricJson = rubric.rubric_data
    const numberOfColumns = rubricJson?.levels?.length

    function addEvaluation(criterium_id, level_id) {

        const evaluationForCriteriumIndex = _.findIndex(rubricEvaluation, { criterium_id })

        if (evaluationForCriteriumIndex === -1) {
            rubricEvaluation = [
                ...rubricEvaluation,
                {
                    level_id,
                    criterium_id
                }
            ];
        } else {

            // When clicking on the same item, do nothing to prevent obsolete backend requests
            if (rubricEvaluation[evaluationForCriteriumIndex].level_id === level_id) {
                return;
            }

            rubricEvaluation = rubricEvaluation.map(item => item.criterium_id === criterium_id
                ? {
                    ...item,
                    level_id,
                }
                : item
            );
        }

        isSyncing = true;
        sync();
    }

    function sync() {

        if (readOnly) {
            return;
        }

        if (xhr) {
            xhr.abort();
            xhr = undefined;
        }

        const cleanData = rubricEvaluation.map(evaluationToClean => {
            if (!evaluationToClean.feedback || evaluationToClean.feedback.trim().length === 0) {
                evaluationToClean.feedback = undefined;
            }

            return evaluationToClean;
        })

        if (evaluation?.id) {

            xhr = jQuery.ajax({
                url: `/rubrics/evaluations/${activity.id}/${rubric.id}/${evaluation.id}.json`,
                type: 'PATCH',
                data: {
                    evaluation_data: cleanData
                },
                contentType: 'application/json',
                success: () => {
                    isSyncing = false;
                }
            });

        } else {

            xhr = jQuery.ajax({
                url: `/rubrics/evaluations/${activity.id}/${rubric.id}.json`,
                type: 'POST',
                data: {
                    user_id: studentId,
                    evaluation_data: cleanData
                },
                contentType: 'application/json',
                success: (savedEvaluation) => {
                    evaluation = savedEvaluation;
                    isSyncing = false;
                }
            });
        }
    }

    const debouncedSync = _.debounce(() => sync(), 400);

    $: evaluationScore = activity.getPointsForRubricEvaluation(rubricEvaluation)
</script>

{#if Array.isArray(rubricJson) && rubricJson.length === 0}
    <StatusCard
        icon="warning"
        color="orange"
    >
        {window.i18n.gettext('Rubric is empty')}
    </StatusCard>
{:else}
    {@const amountTodo = evaluationScore.criteriaCount - evaluationScore.evaluatedCriteriaCount}
    {@const isEmpty = Array.isArray(rubricEvaluation) && rubricEvaluation.length === 0}

    <div class="layout" style:--number-of-columns={numberOfColumns}>
        {#each rubricJson.levels as level, i (i)}
            <div class="level">
                <div class="label">
                    {level.label || ''}
                </div>
                <div class="metadata">
                    ({level.points}p)
                </div>
            </div>
        {/each}

        {#each rubricJson.criteria as criterium, i (i)}

            {@const evaluationForCriteriumIndex = _.findIndex(rubricEvaluation, { criterium_id: criterium.id })}

            <div class="criterium">
                <div class="label">
                    {criterium.label || ''}
                </div>
                <div class="metadata">
                    {Math.round((criterium.weight ?? 0) * 100)}%
                </div>
            </div>

            {#each rubricJson.levels as level, y (y)}
                {@const isActive = rubricEvaluation[evaluationForCriteriumIndex]?.level_id === level.id}

                <button
                    class="description"
                    class:active={isActive}
                    class:empty={isEmpty}
                    on:click={() => !readOnly && addEvaluation(criterium.id, level.id)}
                    tabindex={readOnly ? -1 : 0}
                    disabled={readOnly}
                >
                    {#if !readOnly || isActive}
                        <RadioButton
                            value={level.id}
                            parentGroup={`criterium${i}`}
                            isChecked={isActive}
                            isSmall={true}
                        ></RadioButton>
                    {/if}
                    {criterium.descriptions[level.id] || ''}
                </button>
            {/each}

            {#if readOnly}
                {#if rubricEvaluation[evaluationForCriteriumIndex]?.feedback}
                    <div class="feedback">
                        <span class="label">{window.i18n.gettext('Feedback')}</span>
                        <div class="text">{rubricEvaluation[evaluationForCriteriumIndex]?.feedback}</div>
                    </div>
                {/if}
            {:else}
                {#if evaluationForCriteriumIndex !== -1}
                    <div class="feedback-input">
                        <Textarea
                            minLines="1"
                            placeholder={window.i18n.gettext('Give feedback')}
                            bind:value={rubricEvaluation[evaluationForCriteriumIndex].feedback}
                            inputCallback={() => {
                                isSyncing = true;
                                debouncedSync()
                            }}
                        ></Textarea>
                    </div>
                {/if}
            {/if}
        {/each}

        <div class="summarization">
            <span class="amount-todo">

                {#if !readOnly}
                    {#if isSyncing}
                        {window.i18n.gettext('Saving in progress')}{#if amountTodo > 0},{/if}
                    {:else}
                        {window.i18n.gettext('Saved')}{#if amountTodo > 0},{/if}
                    {/if}
                    {#if amountTodo > 0}
                        {window.i18n.sprintf(
                            window.i18n.ngettext(
                                '%s criterium not evaluated yet',
                                '%s criteria not evaluated yet',
                                amountTodo
                            ),
                            amountTodo
                        )}
                    {/if}
                {/if}
            </span>
            {#if !isEmpty}
                {window.i18n.gettext('Total:')} &nbsp;
                <strong>
                    {window.i18n.sprintf(
                        window.i18n.gettext('%s of %s points'),
                        evaluationScore.score,
                        evaluationScore.total
                    )}
                </strong>
            {/if}
        </div>
    </div>
{/if}

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.layout {
  display: grid;
  grid-template-columns: repeat(var(--number-of-columns), 1fr) auto;
  overflow-x: auto;
  padding-right: 1px;
}
.layout .level,
.layout .criterium {
  display: flex;
}
.layout .level .label,
.layout .criterium .label {
  font-weight: 500;
}
.layout .level .metadata,
.layout .criterium .metadata {
  padding-left: 8px;
}
.layout .level {
  margin-bottom: 8px;
  margin-right: -1px;
  align-items: center;
  background: #fff;
  border: 1px solid hsl(40, 4%, 84%);
  padding: 12px;
}
.layout .criterium {
  margin-top: -1px;
  grid-column: 1/-1;
  margin-right: -1px;
  padding: 12px 0;
}
.layout .description {
  display: flex;
  padding: 12px;
  border: 1px solid hsl(40, 4%, 84%);
  margin-top: -1px;
  background: rgba(255, 255, 255, 0.5);
  color: rgba(18, 45, 64, 0.8);
  margin-right: -1px;
  align-items: flex-start;
  white-space: pre-line;
}
.layout .description.active {
  position: relative;
  border-color: hsl(205, 60%, 44%);
  background: #fff;
}
.layout .description.empty {
  background: #fff;
}
.layout .feedback {
  background: #fff;
  grid-column: 1/3;
  border: 1px solid hsl(40, 4%, 84%);
  border-radius: 16px;
  margin: 8px 0px;
  padding: 12px;
}
.layout .feedback .label {
  font-size: 14px;
  font-variant-caps: all-small-caps;
  letter-spacing: 1px;
  line-height: 12px;
  color: hsl(205, 60%, 44%);
}
.layout .feedback .text {
  font-size: 16px;
  margin-top: 8px;
  white-space: pre-line;
}
.layout .feedback:last-of-type {
  margin-bottom: 0px;
}
.layout .feedback-input {
  grid-column: 1/3;
  margin: 8px 0;
}
.layout .summarization {
  font-size: 16px;
  grid-column: 1/-1;
  margin: 32px 0;
  display: flex;
}
.layout .summarization .amount-todo {
  display: block;
  color: #9a9bb5;
  margin-right: 16px;
}
.layout .summarization .amount-todo:empty {
  display: none;
}</style>
